import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Center,
  CloseButton,
  Container,
  Group,
  Loader,
  Paper,
  Space,
  Title,
} from '@mantine/core';
import {
  EntityIdentifier,
  ObjectMapping,
  objectMappingSelector,
  useCreateObjectMappingMutation,
  useGetObjectMappingQuery,
  useUpdateObjectMappingMutation,
} from '@ots-frontend/ots-data';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { notifications } from '@mantine/notifications';
import {
  ObjectMappingForm,
  ObjectMappingFormValues,
} from '../../components/form/ObjectMappingForm/ObjectMappingForm';
import { useApp } from '../../providers/RegionProvider/RegionProvider';
import { ProtectedPage } from '../../components/page/ProtectedPage/ProtectedPage';

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<ObjectMappingFormValues>;
};

export const ObjectMappingFormPageRoute = () => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const id = state?.id;
  const copy = state?.copy;
  const objectType = searchParams.get('objectType');
  const queryDefaults: Partial<ObjectMappingFormValues> | undefined =
    searchParams.has('kind') ||
    searchParams.has('key') ||
    searchParams.has('value') ||
    objectType
      ? {
          kind: searchParams.get('kind') ?? undefined,
          key: searchParams.get('key') ?? undefined,
          value: searchParams.get('value') ?? undefined,
          object: objectType ? { type: objectType, id: '' } : undefined,
        }
      : undefined;
  const formDefaults = state?.formDefaults ?? queryDefaults;
  return (
    <ObjectMappingRequestFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromObjectMapping = (
  objectMapping: ObjectMapping | undefined
): Partial<ObjectMappingFormValues> => {
  let object: EntityIdentifier | undefined = undefined;

  if (objectMapping && objectMapping.objectId && objectMapping.objectType) {
    object = {
      id: objectMapping.objectId,
      type: objectMapping.objectType,
    };
  }

  return {
    key: objectMapping?.key,
    value: objectMapping?.value,
    object: object,
  };
};

export const ObjectMappingRequestFormPage = (props: Props) => {
  const { id, copy = false, formDefaults } = props;

  const { regionId } = useApp();
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);

  const [access, setAccess] = useState(false);

  const selectObjectMapping = useMemo(() => {
    return objectMappingSelector();
  }, []);

  const { currObjectMapping, isLoading } = useGetObjectMappingQuery(
    access ? id ?? skipToken : skipToken,
    {
      selectFromResult: (result) => ({
        currObjectMapping: selectObjectMapping(result),
        isLoading: result.isLoading,
      }),
    }
  );

  const _formDefaults: Partial<ObjectMappingFormValues> = useMemo(() => {
    return formDefaults
      ? formDefaults
      : toFormDefaultsFromObjectMapping(currObjectMapping);
  }, [currObjectMapping, formDefaults]);

  const isEdit = useMemo(() => {
    return currObjectMapping && !copy;
  }, [currObjectMapping, copy]);

  const title: string = useMemo(() => {
    if (isEdit) {
      return t('page.objectmapping.form.title.edit', 'Edit Object Mapping', {
        ns: 'common',
      });
    } else {
      return t('page.objectmapping.form.title.new', 'New Object Mapping', {
        ns: 'common',
      });
    }
  }, [isEdit, t]);

  const [createObjectMapping, { isLoading: isCreating }] =
    useCreateObjectMappingMutation();
  const [updateObjectMapping, { isLoading: isUpdating }] =
    useUpdateObjectMappingMutation();

  const onObjectMappingSubmit = useCallback(
    (formValues: ObjectMappingFormValues) => {
      if (isEdit) {
        const objectMapping: Partial<ObjectMapping> = {
          ...currObjectMapping,
          key: formValues.key,
          value: formValues.value,
        };
        updateObjectMapping(objectMapping)
          .unwrap()
          .then(() => {
            notifications.show({
              color: 'green',
              title:
                t('common.entity.objectmapping', 'Object Mapping', {
                  ns: 'common',
                }) +
                ' ' +
                t('common.status.updated', 'Updated', { ns: 'common' }),
              message: '',
            });
            navigate(-1);
          })
          .catch((rejected) =>
            notifications.show({
              color: 'red',
              title: t('common.error', 'Error', { ns: 'common' }),
              message:
                rejected?.data?.detail ??
                t('common.error', 'Error', { ns: 'common' }),
            })
          );
      } else {
        const objectMapping: Partial<ObjectMapping> = {
          key: formValues.key,
          value: formValues.value,
          objectId: formValues.object.id,
          objectType: formValues.object.type,
        };
        createObjectMapping(objectMapping)
          .unwrap()
          .then(() => {
            notifications.show({
              color: 'green',
              title:
                t('common.entity.objectmapping', 'Object Mapping', {
                  ns: 'common',
                }) +
                ' ' +
                t('common.status.created', 'Created', { ns: 'common' }),
              message: '',
            });
            navigate(-1);
          })
          .catch((rejected) =>
            notifications.show({
              color: 'red',
              title: t('common.error', 'Error', { ns: 'common' }),
              message:
                rejected?.data?.detail ??
                t('common.error', 'Error', { ns: 'common' }),
            })
          );
      }
    },
    [
      currObjectMapping,
      isEdit,
      updateObjectMapping,
      t,
      navigate,
      createObjectMapping,
    ]
  );

  return (
    <ProtectedPage
      permissions={[isEdit ? 'objectmapping_edit' : 'objectmapping_create']}
      isLoading={isLoading}
      onAccess={setAccess}
    >
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader />}
            {!isLoading && (
              <Paper miw={1000} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">{title}</Title>
                  <CloseButton
                    title="Close"
                    size="xl"
                    iconSize={20}
                    onClick={() => navigate(-1)}
                  />
                </Group>
                <Space h="md" />
                <ObjectMappingForm
                  options={{
                    regionId: regionId,
                    objectSelectDisabled:
                      typeof currObjectMapping !== 'undefined' ||
                      typeof formDefaults?.object !== 'undefined',
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onObjectMappingSubmit}
                />
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};
